/**
 * Featured Products Section
 * Industry-specific product grids with distinct layouts
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGA4 } from '../../contexts/GA4Context';
import { addToCart } from '../../features/cart/cartSlice';
import useDarkModeNeutrals from '../../hooks/useDarkModeNeutrals';
import useStorefrontTheme from '../../hooks/useStorefrontTheme';
import { getStorefrontVariantAvailability } from '../../utils/storefrontVariantUtils';
import './sections.css';

function FeaturedProducts({ settings = {}, content = {}, globalStyles = {}, store, industry }) {
  const dispatch = useDispatch();
  const { trackViewItemList, trackAddToCart } = useGA4() || {};
  // Dark-mode-aware neutral palette. Used as the destructure fallback
  // for neutral color settings below — in light mode the values are
  // identical to the old hardcoded hexes, in dark mode they flip so
  // unconfigured merchants get a working dark theme. Merchants who
  // pinned a custom color in theme settings still win because the
  // destructure default only applies when `settings.<x>` is undefined.
  const neutrals = useDarkModeNeutrals();
  const {
    count = 8,
    columns = 4,
    showPrice = true,
    showQuickAdd = false,
    variant = 'default', // default, lookbook, quickadd, techspec, menucard
    // Granular style settings
    backgroundColor: configuredBackgroundColor,
    titleColor: configuredTitleColor,
    subtitleColor: configuredSubtitleColor,
    productNameColor: configuredProductNameColor,
    priceColor,
    cardBgColor: configuredCardBgColor,
    cardBorderColor: configuredCardBorderColor,
    cardRadius = '12px',
    cardShadow = 'medium',
    quickAddColor,
    darkBackgroundColor,
    darkTitleColor,
    darkSubtitleColor,
    darkProductNameColor,
    darkPriceColor,
    darkCardBgColor,
    darkCardBorderColor,
    darkQuickAddColor,
  } = settings;

  const { resolvedTheme } = useStorefrontTheme();
  const isDark = resolvedTheme === 'dark';
  const backgroundColor = (isDark ? darkBackgroundColor : configuredBackgroundColor) || neutrals.background;
  const titleColor = (isDark ? darkTitleColor : configuredTitleColor) || neutrals.text;
  const subtitleColor = (isDark ? darkSubtitleColor : configuredSubtitleColor) || neutrals.textMuted;
  const productNameColor = (isDark ? darkProductNameColor : configuredProductNameColor) || neutrals.text;
  const cardBgColor = (isDark ? darkCardBgColor : configuredCardBgColor) || neutrals.surface;
  const cardBorderColor = (isDark ? darkCardBorderColor : configuredCardBorderColor) || neutrals.border;

  const {
    title = 'Featured Products',
    subtitle = '',
    productIds = [],
  } = content;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const colors = globalStyles.colors || {};
  const fonts = globalStyles.fonts || {};

  // Use settings colors or fall back to globalStyles
  const effectivePriceColor = (isDark ? darkPriceColor : priceColor) || colors.primary || '#2563eb';
  const effectiveQuickAddColor = (isDark ? darkQuickAddColor : quickAddColor) || colors.primary || '#2563eb';

  // Determine variant based on industry if not explicitly set
  const effectiveVariant = variant !== 'default' ? variant : getIndustryVariant(industry);

  // Depend on stable primitives. `store` and the default `productIds = []`
  // were previously new references every render, which refired this effect
  // forever. We now key the array by its joined string and read store?.slug
  // directly. trackViewItemList is stable thanks to GA4Context's useMemo +
  // module-level no-op fallback (so it's safe to list in deps).
  const productIdsKey = productIds.join(',');
  useEffect(() => {
    const fetchProducts = async () => {
      if (!store?.id && !store?.slug) {
        setLoading(false);
        return;
      }

      try {
        const slug = store.slug;
        const url = productIds.length > 0
          ? `/api/stores/${slug}/products?ids=${productIds.join(',')}`
          : `/api/stores/${slug}/products?limit=${count}&featured=true`;

        const res = await fetch(url);
        const data = await res.json();

        if (data.success && data.products) {
          const loadedProducts = data.products.slice(0, count);
          setProducts(loadedProducts);

          // GA4: Track product list view
          if (trackViewItemList && loadedProducts.length > 0) {
            trackViewItemList(loadedProducts, title || 'Featured Products');
          }
        }
      } catch (err) {
        console.error('[FeaturedProducts] Failed to fetch:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
    // productIds intentionally omitted in favor of productIdsKey (stable string).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.slug, count, productIdsKey, trackViewItemList, title]);

  const formatPrice = (price) => {
    if (!store?.currency) return `₦${price?.toLocaleString() || '0'}`;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: store.currency,
      minimumFractionDigits: 0,
    }).format(price || 0);
  };

  // Handle add to cart with GA4 tracking
  const handleAddToCart = useCallback((product) => {
    dispatch(addToCart({
      productId: product.id,
      quantity: 1,
      storeSlug: store?.slug,
    }));

    // GA4: Track add to cart
    if (trackAddToCart) {
      trackAddToCart(product, 1);
    }
  }, [dispatch, store?.slug, trackAddToCart]);

  // Shadow mapping
  const shadowMap = {
    none: 'none',
    small: '0 1px 3px rgba(0, 0, 0, 0.08)',
    medium: '0 4px 12px rgba(0, 0, 0, 0.08)',
    large: '0 8px 24px rgba(0, 0, 0, 0.12)',
  };

  const gridStyle = {
    '--columns': columns,
    '--primary-color': effectivePriceColor,
    '--text-color': productNameColor,
    '--muted-color': subtitleColor,
    '--font-heading': fonts.heading || 'inherit',
    '--card-bg': cardBgColor,
    '--card-border': cardBorderColor,
    '--card-radius': cardRadius,
    '--card-shadow': shadowMap[cardShadow] || shadowMap.medium,
    '--quick-add-color': effectiveQuickAddColor,
    backgroundColor: backgroundColor,
  };

  const titleStyle = {
    fontFamily: fonts.heading || 'inherit',
    color: titleColor,
  };

  const subtitleStyle = {
    color: subtitleColor,
  };

  if (loading) {
    return (
      <section className={`ts-featured-products ts-featured-products--${effectiveVariant}`}>
        <div className="ts-featured-products__container">
          <div className="ts-featured-products__loading">
            <div className="ts-featured-products__spinner" />
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return (
      <section className={`ts-featured-products ts-featured-products--${effectiveVariant}`}>
        <div className="ts-featured-products__container">
          <div className="ts-featured-products__header">
            <h2 className="ts-featured-products__title" style={titleStyle}>{title}</h2>
          </div>
          <div className="ts-featured-products__empty">
            <p>No products available</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`ts-featured-products ts-featured-products--${effectiveVariant}`} style={gridStyle}>
      <div className="ts-featured-products__container">
        {title && (
          <div className="ts-featured-products__header">
            <h2 className="ts-featured-products__title" style={titleStyle}>
              {title}
            </h2>
            {subtitle && (
              <p className="ts-featured-products__subtitle" style={subtitleStyle}>{subtitle}</p>
            )}
          </div>
        )}

        <div className="ts-featured-products__grid">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              variant={effectiveVariant}
              showPrice={showPrice}
              showQuickAdd={showQuickAdd}
              formatPrice={formatPrice}
              colors={colors}
              onAddToCart={handleAddToCart}
              styleSettings={{
                productNameColor,
                priceColor: effectivePriceColor,
                cardBgColor,
                cardBorderColor,
                cardRadius,
                cardShadow: shadowMap[cardShadow] || shadowMap.medium,
                quickAddColor: effectiveQuickAddColor,
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Get layout variant based on industry
 */
function getIndustryVariant(industry) {
  const industryMap = {
    FASHION: 'lookbook',
    KIDS: 'playful',
    FMCG: 'quickadd',
    TECH: 'techspec',
    RESTAURANT: 'menucard',
    LUXURY: 'luxe',
    HANDMADE: 'artisan',
    SERVICES: 'default',
    AUTOS: 'techspec',
    REAL_ESTATE: 'default',
    HOME_SERVICES: 'default',
    BEAUTY: 'luxe',
    GENERAL: 'default',
  };
  return industryMap[industry] || 'default';
}

/**
 * Product Card - renders differently based on variant
 */
function SaleBadge({ product }) {
  const availability = getStorefrontVariantAvailability(product);
  if (!product.activeSale || !availability.hasStock) return null;
  return (
    <span className="ts-product-sale-badge">
      {product.activeSale.badgeText || 'SALE'}
    </span>
  );
}

function ProductPrice({ product, formatPrice, className, style }) {
  return (
    <span className={className} style={style}>
      {product.compareAtPrice && Number(product.compareAtPrice) > Number(product.price) && (
        <span className="ts-product-compare-price">{formatPrice(product.compareAtPrice)}</span>
      )}
      <span>{formatPrice(product.price)}</span>
    </span>
  );
}

function ProductCard({ product, variant, showPrice, showQuickAdd, formatPrice, colors, onAddToCart, styleSettings = {} }) {
  const cardClass = `ts-product-card ts-product-card--${variant}`;

  switch (variant) {
    case 'lookbook':
      return <LookbookCard product={product} showPrice={showPrice} formatPrice={formatPrice} colors={colors} styleSettings={styleSettings} />;
    case 'quickadd':
      return <QuickAddCard product={product} formatPrice={formatPrice} colors={colors} onAddToCart={onAddToCart} styleSettings={styleSettings} />;
    case 'techspec':
      return <TechSpecCard product={product} showPrice={showPrice} formatPrice={formatPrice} colors={colors} styleSettings={styleSettings} />;
    case 'menucard':
      return <MenuCard product={product} formatPrice={formatPrice} colors={colors} styleSettings={styleSettings} />;
    case 'luxe':
      return <LuxeCard product={product} showPrice={showPrice} formatPrice={formatPrice} colors={colors} styleSettings={styleSettings} />;
    case 'playful':
      return <PlayfulCard product={product} showPrice={showPrice} formatPrice={formatPrice} colors={colors} styleSettings={styleSettings} />;
    default:
      return <DefaultCard product={product} showPrice={showPrice} showQuickAdd={showQuickAdd} formatPrice={formatPrice} colors={colors} onAddToCart={onAddToCart} styleSettings={styleSettings} />;
  }
}

/**
 * Default glassmorphism card
 */
function DefaultCard({ product, showPrice, showQuickAdd, formatPrice, colors, onAddToCart, styleSettings = {} }) {
  const { productNameColor, priceColor, cardBgColor, cardBorderColor, cardRadius, cardShadow, quickAddColor } = styleSettings;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  const cardStyle = {
    backgroundColor: cardBgColor,
    borderColor: cardBorderColor,
    borderRadius: cardRadius,
    boxShadow: cardShadow,
  };

  return (
    <Link to={`/product/${product.slug || product.id}`} className="ts-product-card ts-product-card--default" style={cardStyle}>
      <div className="ts-product-card__image-wrapper">
        <SaleBadge product={product} />
        {product.images?.[0] || product.image ? (
          <img
            src={product.images?.[0] || product.image}
            alt={product.name}
            className="ts-product-card__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-product-card__placeholder">
            <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}
        {showQuickAdd && (
          <button className="ts-product-card__quick-add" onClick={handleQuickAdd} style={{ backgroundColor: quickAddColor }}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        )}
      </div>
      <div className="ts-product-card__info">
        <h3 className="ts-product-card__name" style={{ color: productNameColor }}>{product.name}</h3>
        {showPrice && (
          <ProductPrice product={product} formatPrice={formatPrice} className="ts-product-card__price" style={{ color: priceColor }} />
        )}
      </div>
    </Link>
  );
}

/**
 * Fashion Lookbook Card - Editorial style, large images, minimal text
 */
function LookbookCard({ product, showPrice, formatPrice, colors, styleSettings = {} }) {
  const { productNameColor, priceColor, cardBgColor, cardRadius } = styleSettings;

  const cardStyle = {
    backgroundColor: cardBgColor,
    borderRadius: cardRadius,
  };

  return (
    <Link to={`/product/${product.slug || product.id}`} className="ts-product-card ts-product-card--lookbook" style={cardStyle}>
      <div className="ts-lookbook__image-container">
        <SaleBadge product={product} />
        {product.images?.[0] || product.image ? (
          <img
            src={product.images?.[0] || product.image}
            alt={product.name}
            className="ts-lookbook__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-lookbook__placeholder" />
        )}
        <div className="ts-lookbook__overlay">
          <span className="ts-lookbook__view">View</span>
        </div>
      </div>
      <div className="ts-lookbook__details">
        <h3 className="ts-lookbook__name" style={{ color: productNameColor }}>{product.name}</h3>
        {showPrice && (
          <ProductPrice product={product} formatPrice={formatPrice} className="ts-lookbook__price" style={{ color: priceColor }} />
        )}
      </div>
    </Link>
  );
}

/**
 * FMCG Quick Add Card - Price-forward, prominent Add button
 */
function QuickAddCard({ product, formatPrice, colors, onAddToCart, styleSettings = {} }) {
  const { productNameColor, priceColor, cardBgColor, cardBorderColor, cardRadius, cardShadow, quickAddColor } = styleSettings;
  const availability = getStorefrontVariantAvailability(product);
  const availableQuantity = availability?.availableQuantity ?? 0;
  const isOutOfStock = !availability.hasStock;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOutOfStock) return;
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  const cardStyle = {
    backgroundColor: cardBgColor,
    borderColor: cardBorderColor,
    borderRadius: cardRadius,
    boxShadow: cardShadow,
  };

  return (
    <Link to={`/product/${product.slug || product.id}`} className="ts-product-card ts-product-card--quickadd" style={cardStyle}>
      <div className="ts-quickadd__image-wrapper">
        <SaleBadge product={product} />
        {product.images?.[0] || product.image ? (
          <img
            src={product.images?.[0] || product.image}
            alt={product.name}
            className="ts-quickadd__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-quickadd__placeholder" />
        )}
        {availableQuantity <= 5 && availableQuantity > 0 && (
          <span className="ts-quickadd__stock-badge">Only {availableQuantity} left</span>
        )}
      </div>
      <div className="ts-quickadd__content">
        <h3 className="ts-quickadd__name" style={{ color: productNameColor }}>{product.name}</h3>
        <div className="ts-quickadd__bottom">
          <ProductPrice product={product} formatPrice={formatPrice} className="ts-quickadd__price" style={{ color: priceColor }} />
          <button
            className="ts-quickadd__btn"
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            style={{ backgroundColor: quickAddColor, opacity: isOutOfStock ? 0.55 : 1, cursor: isOutOfStock ? 'not-allowed' : 'pointer' }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {isOutOfStock ? 'Out of Stock' : 'Add'}
          </button>
        </div>
      </div>
    </Link>
  );
}

/**
 * Tech Spec Card - Dark theme, specs badges, modern
 */
function TechSpecCard({ product, showPrice, formatPrice, colors, styleSettings = {} }) {
  const { productNameColor, priceColor, cardBgColor, cardRadius, cardShadow } = styleSettings;
  // Extract specs from product description or tags
  const specs = product.tags?.slice(0, 3) || [];

  const cardStyle = {
    backgroundColor: cardBgColor,
    borderRadius: cardRadius,
    boxShadow: cardShadow,
  };

  return (
    <Link to={`/product/${product.slug || product.id}`} className="ts-product-card ts-product-card--techspec" style={cardStyle}>
      <div className="ts-techspec__image-container">
        <SaleBadge product={product} />
        {product.images?.[0] || product.image ? (
          <img
            src={product.images?.[0] || product.image}
            alt={product.name}
            className="ts-techspec__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-techspec__placeholder">
            <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
              <rect x="5" y="2" width="14" height="20" rx="2" />
              <line x1="12" y1="18" x2="12" y2="18" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        )}
        {product.isNew && <span className="ts-techspec__badge">NEW</span>}
      </div>
      <div className="ts-techspec__content">
        <h3 className="ts-techspec__name" style={{ color: productNameColor }}>{product.name}</h3>
        {specs.length > 0 && (
          <div className="ts-techspec__specs">
            {specs.map((spec, i) => (
              <span key={i} className="ts-techspec__spec">{spec}</span>
            ))}
          </div>
        )}
        {showPrice && (
          <ProductPrice product={product} formatPrice={formatPrice} className="ts-techspec__price" style={{ color: priceColor }} />
        )}
      </div>
    </Link>
  );
}

/**
 * Restaurant Menu Card - Horizontal layout, description visible
 */
function MenuCard({ product, formatPrice, colors, styleSettings = {} }) {
  const { productNameColor, priceColor, cardBgColor, cardBorderColor, cardRadius, cardShadow } = styleSettings;

  const cardStyle = {
    backgroundColor: cardBgColor,
    borderColor: cardBorderColor,
    borderRadius: cardRadius,
    boxShadow: cardShadow,
  };

  return (
    <Link to={`/product/${product.slug || product.id}`} className="ts-product-card ts-product-card--menucard" style={cardStyle}>
      <div className="ts-menucard__image-wrapper">
        <SaleBadge product={product} />
        {product.images?.[0] || product.image ? (
          <img
            src={product.images?.[0] || product.image}
            alt={product.name}
            className="ts-menucard__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-menucard__placeholder">
            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M12 3v18M3 12l3 3-3 3M21 12l-3 3 3 3M9 6v12M15 6v12" />
            </svg>
          </div>
        )}
      </div>
      <div className="ts-menucard__info">
        <div className="ts-menucard__header">
          <h3 className="ts-menucard__name" style={{ color: productNameColor }}>{product.name}</h3>
          <ProductPrice product={product} formatPrice={formatPrice} className="ts-menucard__price" style={{ color: priceColor }} />
        </div>
        {product.description && (
          <p className="ts-menucard__desc">{product.description.slice(0, 80)}...</p>
        )}
      </div>
    </Link>
  );
}

/**
 * Luxury Card - Elegant with gold accents, serif typography
 */
function LuxeCard({ product, showPrice, formatPrice, colors, styleSettings = {} }) {
  const { productNameColor, priceColor, cardBgColor, cardRadius, cardShadow } = styleSettings;

  const cardStyle = {
    backgroundColor: cardBgColor,
    borderRadius: cardRadius,
    boxShadow: cardShadow,
  };

  return (
    <Link to={`/product/${product.slug || product.id}`} className="ts-product-card ts-product-card--luxe" style={cardStyle}>
      <div className="ts-luxe__image-wrapper">
        <SaleBadge product={product} />
        {product.images?.[0] || product.image ? (
          <img
            src={product.images?.[0] || product.image}
            alt={product.name}
            className="ts-luxe__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-luxe__placeholder" />
        )}
        <div className="ts-luxe__shine" />
      </div>
      <div className="ts-luxe__details">
        <h3 className="ts-luxe__name" style={{ color: productNameColor }}>{product.name}</h3>
        {showPrice && (
          <ProductPrice product={product} formatPrice={formatPrice} className="ts-luxe__price" style={{ color: priceColor }} />
        )}
      </div>
    </Link>
  );
}

/**
 * Playful Card - For kids/baby products, rounded, colorful
 */
function PlayfulCard({ product, showPrice, formatPrice, colors, styleSettings = {} }) {
  const { productNameColor, priceColor, cardBgColor, cardRadius, cardShadow, quickAddColor } = styleSettings;

  const cardStyle = {
    backgroundColor: cardBgColor,
    borderRadius: cardRadius,
    boxShadow: cardShadow,
  };

  return (
    <Link to={`/product/${product.slug || product.id}`} className="ts-product-card ts-product-card--playful" style={cardStyle}>
      <div className="ts-playful__image-wrapper">
        <SaleBadge product={product} />
        {product.images?.[0] || product.image ? (
          <img
            src={product.images?.[0] || product.image}
            alt={product.name}
            className="ts-playful__image"
            loading="lazy"
          />
        ) : (
          <div className="ts-playful__placeholder">
            <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <circle cx="9" cy="9" r="1" fill="currentColor" />
              <circle cx="15" cy="9" r="1" fill="currentColor" />
            </svg>
          </div>
        )}
      </div>
      <div className="ts-playful__content">
        <h3 className="ts-playful__name" style={{ color: productNameColor }}>{product.name}</h3>
        {showPrice && (
          <ProductPrice product={product} formatPrice={formatPrice} className="ts-playful__price" style={{ backgroundColor: quickAddColor || priceColor }} />
        )}
      </div>
    </Link>
  );
}

export default React.memo(FeaturedProducts);
